import { createTheme } from '@mui/material/styles';

/**
 * Factory function that creates a complete MUI theme from a theme config object.
 *
 * Bold themes get heavier font weights, larger headings, bigger button padding,
 * and deeper card shadows. The original theme preserves the existing styling.
 *
 * The full config is stored on `theme.custom` so components can access
 * `theme.custom.heroBackground`, `theme.custom.assets`, etc.
 *
 * @param {import('./themeConfigs').ThemeConfig} config - Theme configuration object
 * @returns {import('@mui/material/styles').Theme} Complete MUI theme
 */
export function createEllafinTheme(config) {
  const isBold = config.bold;

  // Heading font weight varies by theme type
  const headingWeight = isBold ? 700 : 600;

  // Button styling varies by theme type
  const buttonPadding = isBold ? '12px 32px' : '14px 32px';
  const buttonFontWeight = isBold ? 700 : 600;

  // Card shadow varies by theme type
  const cardShadow = isBold
    ? '0 6px 24px rgba(0, 0, 0, 0.12)'
    : '0 4px 20px rgba(0, 0, 0, 0.08)';

  // H1 size varies by theme type
  const h1Size = isBold ? '3.25rem' : '2.75rem';

  // Derived primary colors
  const primaryMain = config.primary;

  return createTheme({
    // Store the full config for component access
    custom: { ...config },

    palette: {
      mode: 'light',
      primary: {
        main: primaryMain,
        contrastText: '#ffffff',
      },
      secondary: {
        main: config.accent,
        contrastText: '#ffffff',
      },
      accent: {
        main: config.accent,
        contrastText: '#ffffff',
      },
      background: {
        default: config.id === 'original' ? '#ffffff' : config.cream,
        paper: '#ffffff',
        dark: primaryMain,
        light: config.sectionAlt,
        section: config.sectionAlt,
      },
      text: {
        primary: config.textPrimary,
        secondary: config.textSecondary,
        muted: config.textSecondary,
      },
      error: {
        main: '#ec1313',
        contrastText: '#ffffff',
      },
      divider: '#d7dfea',
      border: {
        main: '#d7dfea',
        input: '#e1e7ef',
        focus: primaryMain,
      },
      muted: {
        main: '#e7eeee',
        text: config.textSecondary,
      },
      chart: {
        1: primaryMain,
        2: '#9b7cb6',
        3: '#90acad',
        4: config.accent,
        5: '#483b68',
      },
      teal: {
        light: '#a3c0c1',
        main: '#90acad',
        dark: '#6f8c8d',
      },
      purple: {
        main: primaryMain,
      },
      grey: {
        50: '#f7fafa',
        100: '#e9ecef',
        200: '#dee2e6',
        300: '#ced4da',
        400: '#adb5bd',
        500: '#6c757d',
        600: '#495057',
        700: '#343a40',
        800: '#212529',
        900: '#1A1A1A',
      },
    },

    typography: {
      fontFamily: '"Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontFamily: '"Playfair Display", "Georgia", serif',
        fontWeight: headingWeight,
        fontSize: h1Size,
        lineHeight: 1.2,
        color: primaryMain,
      },
      h2: {
        fontFamily: '"Playfair Display", "Georgia", serif',
        fontWeight: headingWeight,
        fontSize: '2.25rem',
        lineHeight: 1.3,
        color: primaryMain,
      },
      h3: {
        fontFamily: '"Playfair Display", "Georgia", serif',
        fontWeight: headingWeight,
        fontSize: '1.5rem',
        lineHeight: 1.4,
        color: primaryMain,
      },
      h4: {
        fontFamily: '"Manrope", "Roboto", sans-serif',
        fontWeight: headingWeight,
        fontSize: '1.25rem',
        lineHeight: 1.4,
        color: primaryMain,
      },
      body1: {
        fontFamily: '"Manrope", "Roboto", sans-serif',
        fontSize: '1.1rem',
        lineHeight: 1.6,
        color: config.textPrimary,
      },
      body2: {
        fontFamily: '"Manrope", "Roboto", sans-serif',
        fontSize: '1rem',
        lineHeight: 1.5,
        color: config.textPrimary,
      },
      button: {
        fontFamily: '"Manrope", "Roboto", sans-serif',
        fontWeight: buttonFontWeight,
        textTransform: 'none',
      },
    },

    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '9999px',
            padding: buttonPadding,
            fontSize: '1rem',
            fontWeight: buttonFontWeight,
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
            },
          },
          contained: {
            backgroundColor: primaryMain,
            color: '#ffffff',
          },
          outlined: {
            borderColor: primaryMain,
            color: primaryMain,
            backgroundColor: 'transparent',
            '&:hover': {
              color: config.accent,
              borderColor: config.accent,
              backgroundColor: 'transparent',
            },
          },
          text: {
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none',
            color: primaryMain,
            '&:hover': {
              backgroundColor: 'transparent',
              color: config.accent,
              boxShadow: 'none',
              transform: 'none',
            },
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          color: 'inherit',
        },
        styleOverrides: {
          root: {
            backgroundColor: config.headerBackground,
            color: config.id === 'original' ? primaryMain : '#ffffff',
            borderTop: `4px solid ${config.accent}`,
            borderBottom:
              config.id === 'original' ? '1px solid #d7dfea' : 'none',
            boxShadow: 'none',
          },
          colorInherit: {
            backgroundColor: config.headerBackground,
            color: config.id === 'original' ? primaryMain : '#ffffff',
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            color: config.id === 'original' ? primaryMain : '#ffffff',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
            color: config.textPrimary,
            borderRadius: 8,
            boxShadow: cardShadow,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#d7dfea',
              },
              '&:hover fieldset': {
                borderColor: primaryMain,
              },
              '&.Mui-focused fieldset': {
                borderColor: primaryMain,
              },
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: '#e1e7ef',
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            maxWidth: '1200px',
          },
        },
      },
    },

    shape: {
      borderRadius: 8,
    },
    spacing: 8,
  });
}
